import { addDays, startOfDay } from 'date-fns';
import { FormCtx } from 'vopay';
import {
  getOrdinalWeekdayByDayCode,
  getWeekdayByDayCode,
  isWeekdayCode,
  CUSTOM_UNIT_TO_NAME_OF_FREQUENCY,
  NAME_OF_FREQUENCY_TO_CUSTOM_UNIT,
  RecurrenceEndCondition,
  RecurrenceRule,
} from 'UI';
import { PaymentFieldDefault } from './types';

function isToday(dateStr: string): boolean {
  const date = startOfDay(new Date(Number(dateStr)));
  const today = startOfDay(new Date());
  return date.getTime() === today.getTime();
}

export function paymentRuleFromValues(values: PaymentFieldDefault): RecurrenceRule {
  const preset = values.Recurrence || 'one-time';

  // Frequency defaults to whatever the last preset left it at — only trust it in Custom.
  if (preset !== 'custom') {
    return { preset };
  }

  return {
    preset,
    interval: values.Interval,
    unit: values.Frequency ? NAME_OF_FREQUENCY_TO_CUSTOM_UNIT[values.Frequency] : undefined,
    weekdays: values.ByDay ? values.ByDay.split(',').filter(isWeekdayCode) : undefined,
    monthlyRepeatOn: values.Frequency === 'monthly' && values.ByDay ? 'ordinalWeekday' : 'dayOfMonth',
  };
}

export function paymentEndsFromValues(values: PaymentFieldDefault): RecurrenceEndCondition {
  return values.Ending === 'End Date'
    ? { mode: 'date', date: values.EndDate || '' }
    : { mode: 'occurrences', count: values.EndingAfterPayments || '' };
}

export function refreshByDayForStartDate(ctx: FormCtx<PaymentFieldDefault>, startDate: Date): void {
  const rule = paymentRuleFromValues(ctx.values);
  if (rule.preset === 'weekly') {
    ctx.setFieldValue('ByDay', getWeekdayByDayCode(startDate));
    return;
  }
  const needsOrdinalByDayRefresh =
    rule.preset === 'monthly' ||
    (rule.preset === 'custom' && rule.unit === 'month' && rule.monthlyRepeatOn === 'ordinalWeekday');
  if (needsOrdinalByDayRefresh) {
    ctx.setFieldValue('ByDay', getOrdinalWeekdayByDayCode(startDate));
  }
}

export function applyPaymentRuleChange(ctx: FormCtx<PaymentFieldDefault>, rule: RecurrenceRule): void {
  // Switching from one-time to a recurring preset moves the active date field from
  // TakeFromAccountOn (min today) to StartDate (min tomorrow) — bump it forward if it's still today.
  const wasOneTime = !ctx.values.Recurrence || ctx.values.Recurrence === 'one-time';
  let startDate = ctx.values.StartDate ? new Date(Number(ctx.values.StartDate)) : undefined;
  if (wasOneTime && rule.preset !== 'one-time' && startDate && ctx.values.StartDate && isToday(ctx.values.StartDate)) {
    startDate = addDays(startDate, 1);
    ctx.setFieldValue('StartDate', startDate.getTime().toString());
  }

  // Default "Ends: On" to the day after the start date the first time a recurring preset is
  // picked, instead of leaving it blank.
  if (wasOneTime && rule.preset !== 'one-time' && startDate && !ctx.values.EndDate) {
    ctx.setFieldValue('EndDate', addDays(startDate, 1).getTime().toString());
  }

  ctx.setFieldValue('Recurrence', rule.preset);

  if (rule.preset === 'one-time') {
    ctx.setFieldValue('ScheduleType', 'single');
    ctx.setFieldValue('Frequency', undefined);
    ctx.setFieldValue('Interval', undefined);
    ctx.setFieldValue('ByDay', undefined);
    return;
  }

  ctx.setFieldValue('ScheduleType', 'recurring');

  if (rule.preset !== 'custom') {
    ctx.setFieldValue('Frequency', rule.preset);
    ctx.setFieldValue('Interval', undefined);

    const startDate = ctx.values.StartDate ? new Date(Number(ctx.values.StartDate)) : new Date();
    if (rule.preset === 'monthly') {
      ctx.setFieldValue('ByDay', getOrdinalWeekdayByDayCode(startDate));
    } else if (rule.preset === 'weekly') {
      // "Weekly on {weekday}" anchors to the Payment Date's own weekday — API's FR-1 mapping.
      ctx.setFieldValue('ByDay', getWeekdayByDayCode(startDate));
    } else {
      ctx.setFieldValue('ByDay', undefined);
    }
    return;
  }

  ctx.setFieldValue('Frequency', rule.unit ? CUSTOM_UNIT_TO_NAME_OF_FREQUENCY[rule.unit] : undefined);
  ctx.setFieldValue('Interval', rule.interval);

  if (rule.unit === 'week') {
    ctx.setFieldValue('ByDay', (rule.weekdays || []).join(','));
  } else if (rule.unit === 'month' && rule.monthlyRepeatOn === 'ordinalWeekday') {
    const startDate = ctx.values.StartDate ? new Date(Number(ctx.values.StartDate)) : new Date();
    ctx.setFieldValue('ByDay', getOrdinalWeekdayByDayCode(startDate));
  } else {
    ctx.setFieldValue('ByDay', undefined);
  }
}

export function applyPaymentEndsChange(ctx: FormCtx<PaymentFieldDefault>, ends: RecurrenceEndCondition): void {
  if (ends.mode === 'date') {
    ctx.setFieldValue('Ending', 'End Date');
    ctx.setFieldValue('EndDate', ends.date);
  } else {
    ctx.setFieldValue('Ending', 'Payments');
    ctx.setFieldValue('EndingAfterPayments', ends.count);
  }
}
